import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../lib/adminApi";
import { PageHead, Btn, Sel, TInput, Badge } from "../../components/admin/ui";

const ACCESS = ["PUBLIC", "PURCHASE_REQUIRED", "INNER_CIRCLE", "VAULT_ENTITLEMENT", "RESTRICTED"];

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ accessClass: "PUBLIC", altText: "", title: "", relatedRelease: "" });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    adm.media(filter ? `?mediaType=${filter}` : "").then(setItems).catch(() => {});
  }, [filter]);
  useEffect(refresh, [refresh]);

  const upload = async () => {
    if (!file) return toast.error("Choose a file first.");
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try {
      await adm.uploadMedia(fd);
      toast.success("Uploaded.");
      setFile(null);
      refresh();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const setAccess = async (m, v) => {
    try { await adm.updateMedia(m.id, { accessClass: v }); refresh(); toast.success("Access updated."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title || m.originalFilename}"? References will stop resolving.`)) return;
    try { await adm.deleteMedia(m.id); refresh(); toast.success("Deleted."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="admin-media">
      <PageHead title="Media Library" sub="Protected files (purchased audio, downloads, booklets) are never exposed by public URL — access follows the asset's classification, not its file type." />

      <div className="border border-white/10 bg-[#0a0a0a] p-6 mb-8">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-4">UPLOAD</p>
        <div className="grid md:grid-cols-5 gap-4 items-end">
          <div className="md:col-span-2">
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="media-file-input"
              className="text-xs text-white/60 file:bg-white/10 file:border-0 file:text-white/80 file:font-mono file:text-[10px] file:px-3 file:py-2 file:mr-3" />
            <p className="font-mono text-[8px] text-white/30 mt-2">IMAGES ≤10MB · AUDIO ≤60MB · PDF ≤25MB · VIDEO ≤100MB</p>
          </div>
          <Sel label="ACCESS" value={meta.accessClass} onChange={(v) => setMeta({ ...meta, accessClass: v })}
            options={ACCESS.map((a) => ({ v: a, l: a }))} testid="media-access" />
          <TInput label="TITLE" value={meta.title} onChange={(v) => setMeta({ ...meta, title: v })} testid="media-title" />
          <Btn onClick={upload} disabled={busy} testid="media-upload-btn">{busy ? "UPLOADING…" : "UPLOAD"}</Btn>
        </div>
      </div>

      <div className="flex gap-2 mb-5">
        {["", "image", "audio", "video", "document"].map((t) => (
          <button key={t} onClick={() => setFilter(t)}
            className={`font-mono text-[9px] tracking-[0.2em] px-3 py-1.5 border ${filter === t ? "border-gold text-gold" : "border-white/15 text-white/50"}`}>
            {(t || "ALL").toUpperCase()}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((m) => (
          <div key={m.id} className="border border-white/10 bg-[#0a0a0a] p-3" data-testid={`media-item-${m.id}`}>
            {m.mediaType === "image" && m.url ? (
              <img src={m.url} alt={m.altText || ""} className="w-full aspect-square object-cover mb-3" />
            ) : (
              <div className="w-full aspect-square bg-[#101010] flex flex-col items-center justify-center mb-3 gap-2">
                <span className="font-mono text-[10px] text-white/40">{m.mediaType?.toUpperCase()}</span>
                {m.accessClass !== "PUBLIC" && <span className="font-mono text-[8px] text-gold/60">PROTECTED</span>}
              </div>
            )}
            <p className="text-[11px] text-white/80 truncate">{m.title || m.originalFilename}</p>
            <p className="font-mono text-[8px] text-white/30 mt-1">
              {(m.size / 1024 / 1024).toFixed(2)}MB{m.width ? ` · ${m.width}×${m.height}` : ""} · {new Date(m.uploadedAt).toLocaleDateString()}
            </p>
            {m.relatedRelease && <Badge tone="dim">{m.relatedRelease}</Badge>}
            <div className="flex items-center justify-between mt-3 gap-2">
              <select value={m.accessClass} onChange={(e) => setAccess(m, e.target.value)}
                className="bg-[#0d0d0d] border border-white/15 text-[9px] font-mono text-white/70 px-1.5 py-1 flex-1">
                {ACCESS.map((a) => <option key={a}>{a}</option>)}
              </select>
              <button onClick={() => remove(m)} className="text-red-400/70 hover:text-red-400 text-xs px-1" data-testid={`media-delete-${m.id}`}>✕</button>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && <p className="text-white/40 text-sm">No media yet. Upload artwork, photography, audio previews or protected files above.</p>}
    </div>
  );
}
